/**
 * Lightweight JSON-RPC client for the Equabot Gateway WebSocket API.
 *
 * Protocol reference: equabot-gateway/ui/src/ui/gateway.ts (GatewayBrowserClient)
 * Implements device identity (Ed25519) + role/scopes required by the Gateway handshake.
 */

import type { ConnectionStatus, GatewayHelloOk } from '../types'
import {
  loadOrCreateDeviceIdentity,
  signDevicePayload,
  buildDeviceAuthPayload,
  isSecureContext,
  DeviceIdentity,
} from './device-identity'

export type { ConnectionStatus }

export type GatewayRpcErrorInfo = {
  code: string
  message: string
  details?: unknown
}

/**
 * Structured error from a Gateway RPC response (ok: false).
 * Preserves the error `code` for downstream logic (e.g. detecting INVALID_REQUEST
 * on baseHash conflicts in config.patch).
 */
export class GatewayRpcError extends Error {
  readonly code: string
  readonly details?: unknown

  constructor(info: GatewayRpcErrorInfo) {
    super(info.message)
    this.name = 'GatewayRpcError'
    this.code = info.code
    this.details = info.details
  }
}

type Pending = {
  resolve: (value: unknown) => void
  reject: (err: Error) => void
}

type ConnectionChangeCallback = (status: ConnectionStatus, error?: string) => void

const INITIAL_BACKOFF_MS = 500
const MAX_BACKOFF_MS = 15_000
const BACKOFF_FACTOR = 1.7

// Delay before sending the connect message, giving the gateway time to send
// a connect.challenge event first (matches gateway.ts:295-297 queueConnect timer)
const CONNECT_SEND_DELAY_MS = 750

const CONNECT_FAILED_CLOSE_CODE = 4008
const CLOSE_REASON_MAX_BYTES = 120

const CLIENT_ID = 'equabot-control-ui'
const CLIENT_MODE = 'webchat'
const ROLE = 'operator'
const SCOPES = ['operator.admin']

let nextReqId = 1
function generateRequestId(): string {
  return 'rpc-' + String(nextReqId++) + '-' + Math.random().toString(36).slice(2, 8)
}

function truncateUtf8(input: string, maxBytes: number): string {
  let bytes = 0
  let output = ''
  for (const char of input) {
    const codePoint = char.codePointAt(0) ?? 0
    const charBytes = codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4
    if (bytes + charBytes > maxBytes) break
    bytes += charBytes
    output += char
  }
  return output
}

function closeWebSocket(ws: WebSocket, code?: number, reason?: string): void {
  const safeReason = reason ? truncateUtf8(reason, CLOSE_REASON_MAX_BYTES) : ''
  try {
    if (safeReason) {
      ws.close(code, safeReason)
    } else {
      ws.close(code)
    }
  } catch {
    try {
      ws.close(code)
    } catch {
      ws.close()
    }
  }
}

// Device auth token cache (mirrors equabot-gateway/ui/src/ui/device-auth.ts)
const DEVICE_AUTH_STORAGE_KEY = 'equabot.device.auth.v1'
type DeviceAuthEntry = { token: string; role: string; scopes: string[] }
type DeviceAuthStore = { version: 1; deviceId: string; tokens: Record<string, DeviceAuthEntry> }

function loadDeviceAuthToken(deviceId: string, role: string): string | null {
  try {
    const raw = localStorage.getItem(DEVICE_AUTH_STORAGE_KEY)
    if (!raw) return null
    const store = JSON.parse(raw) as DeviceAuthStore
    if (store?.version !== 1 || store.deviceId !== deviceId) return null
    return store.tokens?.[role]?.token ?? null
  } catch { return null }
}

function storeDeviceAuthToken(deviceId: string, role: string, token: string, scopes: string[]) {
  try {
    const existing = (() => {
      try {
        const raw = localStorage.getItem(DEVICE_AUTH_STORAGE_KEY)
        if (!raw) return null
        const s = JSON.parse(raw) as DeviceAuthStore
        return (s?.version === 1 && s.deviceId === deviceId) ? s : null
      } catch { return null }
    })()
    const store: DeviceAuthStore = {
      version: 1,
      deviceId,
      tokens: { ...(existing?.tokens ?? {}), [role]: { token, role, scopes } },
    }
    localStorage.setItem(DEVICE_AUTH_STORAGE_KEY, JSON.stringify(store))
  } catch { /* best-effort */ }
}

function clearDeviceAuthToken(deviceId: string, role: string) {
  try {
    const raw = localStorage.getItem(DEVICE_AUTH_STORAGE_KEY)
    if (!raw) return
    const store = JSON.parse(raw) as DeviceAuthStore
    if (store?.version !== 1 || store.deviceId !== deviceId) return
    if (!store.tokens?.[role]) return
    const next = { ...store, tokens: { ...store.tokens } }
    delete next.tokens[role]
    localStorage.setItem(DEVICE_AUTH_STORAGE_KEY, JSON.stringify(next))
  } catch { /* best-effort */ }
}

export class GatewayRpcClient {
  private ws: WebSocket | null = null
  private pending = new Map<string, Pending>()
  private stopped = true
  private backoffMs = INITIAL_BACKOFF_MS
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private connectTimer: ReturnType<typeof setTimeout> | null = null
  private connectSent = false
  private connectNonce: string | null = null
  private listeners = new Set<ConnectionChangeCallback>()
  private _status: ConnectionStatus = 'disconnected'
  private _url = ''
  private _token: string | undefined
  private _password: string | undefined

  get connected(): boolean {
    return this._status === 'connected'
  }

  get status(): ConnectionStatus {
    return this._status
  }

  connect(url: string, token?: string, password?: string): void {
    this.teardown()
    this._url = url
    this._token = token
    this._password = password
    this.stopped = false
    this.backoffMs = INITIAL_BACKOFF_MS
    this.openSocket()
  }

  disconnect(): void {
    this.teardown()
    this.setStatus('disconnected')
  }

  call<T = unknown>(method: string, params?: unknown): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('gateway not connected'))
    }
    const id = generateRequestId()
    const frame = { type: 'req' as const, id, method, params }
    const promise = new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject })
    })
    this.ws.send(JSON.stringify(frame))
    return promise
  }

  onConnectionChange(cb: ConnectionChangeCallback): () => void {
    this.listeners.add(cb)
    return () => { this.listeners.delete(cb) }
  }

  // --- internal helpers ---

  private teardown(): void {
    this.stopped = true
    this.clearTimers()
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.onopen = null
      this.ws.close()
      this.ws = null
    }
    this.flushPending(new Error('client disconnected'))
  }

  private setStatus(status: ConnectionStatus, error?: string): void {
    const prev = this._status
    this._status = status
    if (prev !== status || error) {
      this.listeners.forEach((cb) => {
        try { cb(status, error) } catch { /* swallow listener errors */ }
      })
    }
  }

  private openSocket(): void {
    if (this.stopped) return
    this.setStatus('connecting')

    try {
      this.ws = new WebSocket(this._url)
    } catch (err) {
      this.setStatus('error', err instanceof Error ? err.message : 'WebSocket creation failed')
      this.scheduleReconnect()
      return
    }

    this.ws.onopen = () => this.queueConnectMessage()
    this.ws.onmessage = (ev) => this.handleMessage(String(ev.data ?? ''))
    this.ws.onclose = (ev) => {
      const reason = String(ev.reason || '')
      this.ws = null
      this.flushPending(new Error(`gateway closed (${ev.code}): ${reason}`))
      if (!this.stopped) {
        this.setStatus('error', reason || `Connection closed (${ev.code})`)
        this.scheduleReconnect()
      }
    }
    this.ws.onerror = () => { /* close handler fires after error */ }
  }

  private queueConnectMessage(): void {
    this.connectNonce = null
    this.connectSent = false
    if (this.connectTimer !== null) clearTimeout(this.connectTimer)
    this.connectTimer = setTimeout(() => {
      this.connectTimer = null
      void this.sendConnectMessage()
    }, CONNECT_SEND_DELAY_MS)
  }

  private async sendConnectMessage(): Promise<void> {
    if (this.connectSent) return
    this.connectSent = true
    if (this.connectTimer !== null) {
      clearTimeout(this.connectTimer)
      this.connectTimer = null
    }

    let deviceIdentity: DeviceIdentity | null = null
    let authToken = this._token
    let canFallbackToShared = false

    if (isSecureContext()) {
      try {
        deviceIdentity = await loadOrCreateDeviceIdentity()
        const cachedToken = loadDeviceAuthToken(deviceIdentity.deviceId, ROLE)
        if (cachedToken) {
          canFallbackToShared = Boolean(this._token)
          authToken = cachedToken
        }
      } catch {
        // Fall back to token-only auth
      }
    }

    const auth = (authToken || this._password)
      ? { token: authToken, password: this._password }
      : undefined

    let device: {
      id: string
      publicKey: string
      signature: string
      signedAt: number
      nonce: string | undefined
    } | undefined

    if (deviceIdentity) {
      const signedAtMs = Date.now()
      const nonce = this.connectNonce ?? undefined
      const payload = buildDeviceAuthPayload({
        deviceId: deviceIdentity.deviceId,
        clientId: CLIENT_ID,
        clientMode: CLIENT_MODE,
        role: ROLE,
        scopes: SCOPES,
        signedAtMs,
        token: authToken ?? null,
        nonce,
      })

      try {
        const signature = await signDevicePayload(deviceIdentity.privateKey, payload)
        device = {
          id: deviceIdentity.deviceId,
          publicKey: deviceIdentity.publicKeyBase64Url,
          signature,
          signedAt: signedAtMs,
          nonce,
        }
      } catch {
        // Signing failed, proceed without device
      }
    }

    const params = {
      minProtocol: 3,
      maxProtocol: 3,
      client: {
        id: CLIENT_ID,
        displayName: 'Equa Web Settings',
        version: '1.0',
        platform: 'web',
        mode: CLIENT_MODE,
      },
      role: ROLE,
      scopes: SCOPES,
      device,
      auth,
      caps: [] as string[],
      userAgent: navigator.userAgent,
      locale: navigator.language,
    }

    this.call<GatewayHelloOk>('connect', params)
      .then((hello) => {
        if (hello?.auth?.deviceToken && deviceIdentity) {
          storeDeviceAuthToken(
            deviceIdentity.deviceId,
            hello.auth.role ?? ROLE,
            hello.auth.deviceToken,
            hello.auth.scopes ?? [],
          )
        }
        this.backoffMs = INITIAL_BACKOFF_MS
        this.setStatus('connected')
      })
      .catch((err) => {
        if (canFallbackToShared && deviceIdentity) {
          clearDeviceAuthToken(deviceIdentity.deviceId, ROLE)
        }
        const message = err instanceof Error ? err.message : 'connect failed'
        const code = err instanceof GatewayRpcError ? err.code : 'CONNECT_FAILED'
        const reason = `${code}: ${message}`
        if (this.ws) closeWebSocket(this.ws, CONNECT_FAILED_CLOSE_CODE, reason)
      })
  }

  private handleMessage(raw: string): void {
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>
    } catch {
      return
    }

    if (parsed.type === 'event') {
      if (parsed.event === 'connect.challenge') {
        const payload = parsed.payload as { nonce?: unknown } | undefined
        const nonce = payload && typeof payload.nonce === 'string' ? payload.nonce : null
        if (nonce) {
          this.connectNonce = nonce
        }
        void this.sendConnectMessage()
        return
      }
      return
    }

    if (parsed.type === 'res') {
      const id = parsed.id as string
      const p = this.pending.get(id)
      if (!p) return
      this.pending.delete(id)
      if (parsed.ok) {
        p.resolve(parsed.payload)
      } else {
        const error = parsed.error as GatewayRpcErrorInfo | undefined
        p.reject(error ? new GatewayRpcError(error) : new Error('request failed'))
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return
    this.clearTimers()
    const delay = this.backoffMs
    this.backoffMs = Math.min(this.backoffMs * BACKOFF_FACTOR, MAX_BACKOFF_MS)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.openSocket()
    }, delay)
  }

  private flushPending(err: Error): void {
    for (const [, p] of this.pending) p.reject(err)
    this.pending.clear()
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.connectTimer !== null) {
      clearTimeout(this.connectTimer)
      this.connectTimer = null
    }
  }
}
